<template>
  <section class="portal">
    <modatta-sidenav page="saas"></modatta-sidenav>
    <main>
      <div class="saas-statistics components-wrapper">
        <modatta-navbar v-bind:title="$t('saas.statistics.title')" :has-icon="{ name: 'activity' }"></modatta-navbar>
        <div class="components">
          <div v-if="isLoading" class="loading component main-table">
            <div class="is-loading loading-row" v-for="(row, index) in 7" :key="index"></div>
          </div>
          <div v-else class="component">
            <div class="metric-cards-row">
              <div class="metric-card">
                <strong :class="{ 'is-loading': !usersRegistered }">{{ usersRegistered || '0' }}</strong>
                <span>{{ $t('saas.statistics.users_registered') }}</span>
              </div>
              <div class="metric-card">
                <strong :class="{ 'is-loading': !clientsRegistered }">{{ clientsRegistered || '0' }}</strong>
                <span>{{ $t('saas.statistics.clients_registered') }}</span>
              </div>
              <div class="metric-card">
                <strong :class="{ 'is-loading': !salesRegistered }">{{ salesRegistered || '0' }}</strong>
                <span>{{ $t('saas.statistics.sales_registered') }}</span>
              </div>
              <div class="metric-card">
                <strong :class="{ 'is-loading': salesValue == null }">{{ formatSalesValue(salesValue) }}</strong>
                <span>{{ $t('saas.statistics.sales_value') }}</span>
              </div>
            </div>

            <!-- Funnel Component -->
            <div class="funnel-section" v-if="false">
              <div class="saas-funnel-container">
                <div
                  v-for="(card, index) in funnelItems"
                  :key="index"
                  class="funnel-card"
                  :class="{ 'has-connector': index > 0 }"
                >
                  <div class="funnel-card-content">
                    <div class="funnel-icon" :style="getIconStyle(card.media)"></div>
                    <div class="funnel-text">
                      <div class="funnel-value">{{ card.title || '0' }}</div>
                      <div class="funnel-label">{{ $te(card.body) ? $t(card.body) : ($te('dashboard.' + card.body) ? $t('dashboard.' + card.body) : card.body) }}</div>
                    </div>
                    <div v-if="card.detail && card.detail.value" class="funnel-percentage" :style="{ backgroundColor: card.detail.background, color: card.detail.percentageColor }">
                      {{ card.detail.value }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabs Section -->
            <div class="tabs-section component">
              <div class="modatta-snap-tabs" ref="tabGroup">
                <!-- Tab Header -->
                <div class="snap-tabs-header">
                  <!-- Tab Navigation -->
                  <div class="snap-tabs-nav" ref="tabNav">
                    <span class="nav-indicator" :style="{ left: tabIndicatorOffset, width: tabIndicatorWidth }"></span>
                    <div v-for="tab in tabs" :key="tab.id">
                      <a
                        :class="{ active: tab.isActive }"
                        ref="tabNavItems"
                        :href="`#${tab.id}`"
                        :active="tab.isActive"
                        @click="
                          (e) => {
                            activateTab(tab);
                            handleTabNavigation(e);
                          }
                        "
                      >
                        {{ $t('saas.statistics.' + tab.name) }}
                      </a>
                    </div>
                  </div>
                </div>
                <!-- Tab Content -->
                <div class="snap-tabs-body">
                  <div class="snap-tab" v-for="tab in tabs" :key="tab.id" v-show="tab.isActive">
                    <!-- Tab: Consultor -->
                    <template v-if="tab.id === 'consultor'">
                      <div class="consultor-tab-content">
                        <div v-if="isLoadingUsers" class="loading component main-table">
                          <div class="is-loading loading-row" v-for="(row, index) in 5" :key="index"></div>
                        </div>
                        <div v-else-if="usersList.length === 0" class="empty-state">
                          <p>{{ $t('saas.statistics.users_empty') }}</p>
                        </div>
                        <div v-else class="users-list">
                          <div
                            v-for="(user, index) in usersList"
                            :key="user.b2c_id || user.external_id || index"
                            class="modatta-accordion user-item"
                            :class="{ 'is-open': usersExpanded[index] }"
                          >
                            <div class="accordion-header" @click="toggleUserExpanded(index)">
                              <div class="user-header-content">
                                <div class="user-info-row">
                                  <div class="user-info-item">
                                    <span class="info-label">{{ $t('saas.statistics.user_email') }}:</span>
                                    <span class="info-value">{{ user.pii && user.pii.email ? user.pii.email : '-' }}</span>
                                  </div>
                                  <div class="user-info-item">
                                    <span class="info-label">{{ $t('saas.statistics.user_phone') }}:</span>
                                    <span class="info-value">{{ user.pii && user.pii.phone ? user.pii.phone : '-' }}</span>
                                  </div>
                                  <div class="user-info-item">
                                    <span class="info-label">{{ $t('saas.statistics.user_tax_id') }}:</span>
                                    <span class="info-value">{{ user.pii && user.pii.tax_id ? user.pii.tax_id : '-' }}</span>
                                  </div>
                                </div>
                              </div>
                              <modatta-icon icon="arrow-down"></modatta-icon>
                            </div>
                            <div class="accordion-body" v-if="usersExpanded[index]">
                              <div class="user-details">
                                <div v-if="isLoadingUserStats[index]" class="loading-stats">
                                  <div class="is-loading loading-row"></div>
                                </div>
                                <div v-else-if="userStats[index]" class="detail-section">
                                  <div class="section-name">{{ $t('saas.statistics.user_statistics') }}</div>
                                  <div class="detail-row">
                                    <span class="detail-label">{{ $t('saas.statistics.user_clients_count') }}:</span>
                                    <span class="detail-value">{{ userStats[index].clients_count || '0' }}</span>
                                  </div>
                                  <div class="detail-row">
                                    <span class="detail-label">{{ $t('saas.statistics.user_sales_count') }}:</span>
                                    <span class="detail-value">{{ userStats[index].sales_count || '0' }}</span>
                                  </div>
                                  <div class="detail-row">
                                    <span class="detail-label">{{ $t('saas.statistics.user_sales_total_value') }}:</span>
                                    <span class="detail-value">{{ formatSaleValue(userStats[index].sales_total_value) }}</span>
                                  </div>
                                </div>
                                <div v-else class="detail-section">
                                  <div class="section-name">{{ $t('saas.statistics.user_statistics') }}</div>
                                  <div class="detail-row">
                                    <span class="detail-value">{{ $t('saas.statistics.no_data_available') }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Tab: Registrations -->
                    <template v-if="tab.id === 'registrations'">
                      <div class="registrations-tab-content">
                        <!-- Filters Section -->
                        <div class="registrations-controls">
                          <div class="quick-filters">
                            <button
                              v-for="period in ['7d', '30d', '90d', 'month']"
                              :key="period"
                              :class="{ active: selectedPeriod === period }"
                              @click="selectPeriod(period)"
                              class="filter-btn"
                            >
                              {{ $t('saas.statistics.registrations_quick_filter_' + period) }}
                            </button>
                          </div>
                          <div class="granularity-controls">
                            <button
                              v-for="granularity in ['daily', 'weekly', 'monthly']"
                              :key="granularity"
                              :class="{ active: selectedGranularity === granularity }"
                              @click="selectGranularity(granularity)"
                              class="granularity-btn"
                            >
                              {{ $t('saas.statistics.registrations_granularity_' + granularity) }}
                            </button>
                          </div>
                        </div>

                        <!-- Chart Section -->
                        <div v-if="isLoadingRegistrations" class="loading component chart-loading">
                          <div class="is-loading loading-row" v-for="(row, index) in 5" :key="index"></div>
                        </div>
                        <div v-else-if="registrationsData.length === 0" class="empty-state">
                          <p>{{ $t('saas.statistics.registrations_empty') }}</p>
                        </div>
                        <div v-else class="chart-container">
                          <div class="chart-wrapper">
                            <canvas ref="registrationsChart"></canvas>
                          </div>
                        </div>

                        <!-- Summary Cards -->
                        <div v-if="registrationsData.length > 0" class="summary-cards">
                          <div class="summary-card">
                            <div class="summary-value">{{ registrationsSummary.total }}</div>
                            <div class="summary-label">{{ $t('saas.statistics.registrations_total') }}</div>
                          </div>
                          <div class="summary-card">
                            <div class="summary-value">{{ registrationsSummary.average.toFixed(1) }}</div>
                            <div class="summary-label">{{ $t('saas.statistics.registrations_average') }}</div>
                          </div>
                          <div class="summary-card">
                            <div class="summary-value">{{ registrationsSummary.peak }}</div>
                            <div class="summary-label">{{ $t('saas.statistics.registrations_peak') }}</div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Tab: Missions -->
                    <template v-if="tab.id === 'missions'">
                      <div class="missions-tab-content">
                        <div v-if="isLoadingMissions" class="loading component main-table">
                          <div class="is-loading loading-row" v-for="(row, index) in 5" :key="index"></div>
                        </div>
                        <div v-else-if="!missionsData || !missionsData.levels || missionsData.levels.length === 0" class="empty-state">
                          <p>{{ $t('saas.statistics.missions_empty') }}</p>
                        </div>
                        <div v-else class="missions-list">
                          <div
                            v-for="(levelData, levelIndex) in missionsData.levels"
                            :key="levelData.level.id"
                            class="modatta-accordion level-item"
                            :class="{ 'is-open': levelsExpanded[levelIndex] }"
                          >
                            <div class="accordion-header" @click="toggleLevelExpanded(levelIndex)">
                              <div class="level-header-content">
                                <div class="level-title">
                                  <span class="level-name">Level {{ levelData.level.index }}</span>
                                  <span class="level-missions-count">({{ levelData.offers.length }} {{ levelData.offers.length === 1 ? 'missão' : 'missões' }})</span>
                                </div>
                                <div class="level-stats">
                                  <div class="stat-item">
                                    <span class="stat-label">Aceitas:</span>
                                    <span class="stat-value">{{ getLevelTotalAccepted(levelData.offers) }}</span>
                                  </div>
                                  <div class="stat-item">
                                    <span class="stat-label">Validadas:</span>
                                    <span class="stat-value">{{ getLevelTotalValidated(levelData.offers) }}</span>
                                  </div>
                                </div>
                              </div>
                              <modatta-icon icon="arrow-down"></modatta-icon>
                            </div>
                            <div class="accordion-body" v-if="levelsExpanded[levelIndex]">
                              <div v-if="levelData.offers.length === 0" class="empty-missions">
                                <p>{{ $t('saas.statistics.missions_empty_level') }}</p>
                              </div>
                              <div v-else class="missions-cards">
                                <div
                                  v-for="offer in getSortedOffers(levelData.offers)"
                                  :key="offer.id"
                                  class="mission-card"
                                >
                                  <div class="mission-name">{{ offer.name }}</div>
                                  <div class="mission-stats">
                                    <div class="mission-stat">
                                      <span class="mission-stat-label">Aceitas</span>
                                      <span class="mission-stat-value">{{ offer.decisions_accepted_count }}</span>
                                    </div>
                                    <div class="mission-stat">
                                      <span class="mission-stat-label">Validadas</span>
                                      <span class="mission-stat-value">{{ offer.decisions_validated_count }}</span>
                                    </div>
                                    <div class="mission-status">
                                      <span class="status-badge" :class="offer.status_id ? 'status-' + offer.status_id.toLowerCase() : ''">
                                        {{ getOfferStatusText(offer.status_id) }}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import ModattaSidenav from '../../../components/ModattaSidenav.vue';
  import ModattaNavbar from '../../../components/ModattaNavbar.vue';
  import ModattaIcon from '../../../components/ModattaIcon.vue';
  import Utilities from '../../../helpers/utilities.js';
  import SaasAPI from '../../../api/saas_api.js';
  import tinycolor from 'tinycolor2';
  import Chart from 'chart.js';
  import SetOfferStatus from '../../../helpers/set-offer-status.js';

  export default {
    components: { ModattaSidenav, ModattaNavbar, ModattaIcon },
    data() {
      return {
        isLoading: true,
        usersRegistered: null,
        clientsRegistered: null,
        salesRegistered: null,
        salesValue: null,
        funnelData: null,
        delivered: null,
        tabs: [
          { id: 'missions', name: 'tabs_metricas', isActive: true },
          { id: 'consultor', name: 'tabs_consultor', isActive: false },
          { id: 'registrations', name: 'tabs_registrations', isActive: false }
        ],
        tabIndicatorOffset: 0,
        tabIndicatorWidth: 0,
        usersList: [],
        usersExpanded: {},
        isLoadingUsers: false,
        userStats: {},
        isLoadingUserStats: {},
        // Registrations chart data
        isLoadingRegistrations: false,
        registrationsData: [],
        selectedPeriod: '30d',
        selectedGranularity: 'daily',
        customStartDate: null,
        customEndDate: null,
        chartData: null,
        chartInstance: null,
        registrationsSummary: {
          total: 0,
          average: 0,
          peak: 0,
        },
        // Missions data
        isLoadingMissions: false,
        missionsData: null,
        levelsExpanded: {},
        funnelItems: [
          {
            tooltip: 'delivered_tooltip',
            body: 'saas.statistics.missions_created',
            title: null,
            media: { type: 'icon', source: 'send', color: '#7494EA', hasShadow: true },
          },
          {
            tooltip: 'accepted_tooltip',
            layout: 'horizontal',
            detail: { percentageColor: '#19D2B4', background: '#DDF8F4', value: '' },
            body: 'accepted_counter_label',
            title: null,
            media: { type: 'icon', source: 'thumbs-up', color: '#19D2B4', hasShadow: true },
          },
          {
            tooltip: 'validated_tooltip',
            layout: 'horizontal',
            detail: { percentageColor: '#19D2B4', background: '#DDF8F4', value: '' },
            body: 'validated_counter_label',
            title: null,
            media: { type: 'icon', source: 'check', color: '#19D2B4', hasShadow: true },
          },
          {
            tooltip: 'evaluated_tooltip',
            detail: { percentageColor: '#19D2B4', background: '#DDF8F4', value: '' },
            body: 'saas.statistics.completed',
            title: null,
            media: { type: 'icon', source: 'star', color: '#19D2B4', hasShadow: true },
          },
        ],
      };
    },
    mounted() {
      this.loadStatistics();
      // Load initial tab data and position indicator
      this.$nextTick(() => {
        this.initializeTabIndicator();
        // Load data for the default active tab
        const activeTab = this.tabs.find(t => t.isActive) || this.tabs[0];
        if (activeTab.id === 'missions' && !this.missionsData) {
          this.loadMissionsData();
        }
        if (activeTab.id === 'consultor' && this.usersList.length === 0) {
          this.loadUsersList();
        }
      });
    },
    beforeDestroy() {
      // Cleanup chart instance
      if (this.chartInstance) {
        this.chartInstance.destroy();
      }
    },
    computed: {
      ...mapState(['user']),
    },
    methods: {
      async loadStatistics() {
        try {
          this.isLoading = true;
          const saasId = this.$route.params.id;

          if (saasId) {
            // Fetch users count
            const usersResponse = await SaasAPI.getUsersCount(saasId);
            console.log('Users Count API Response:', usersResponse.data);

            if (usersResponse.data && usersResponse.data.users_count !== undefined) {
              this.usersRegistered = usersResponse.data.users_count;
            }

            // Fetch clients count
            const clientsResponse = await SaasAPI.getClientsCount(saasId);
            console.log('Clients Count API Response:', clientsResponse.data);

            if (clientsResponse.data && clientsResponse.data.clients_count !== undefined) {
              this.clientsRegistered = clientsResponse.data.clients_count;
            }

            // Fetch sales count
            const salesResponse = await SaasAPI.getSalesCount(saasId);
            console.log('Sales Count API Response:', salesResponse.data);

            if (salesResponse.data) {
              if (salesResponse.data.sales_count !== undefined) {
                this.salesRegistered = salesResponse.data.sales_count;
              }
              if (salesResponse.data.sales_sum !== undefined) {
                this.salesValue = salesResponse.data.sales_sum;
              }
            }

            // Fetch offers stats for funnel (after usersRegistered is loaded)
            await this.loadOffersStats(saasId);

            // Recalculate percentages after all data is loaded
            this.recalculateFunnelPercentages();
          }
        } catch (error) {
          console.error('Error loading statistics:', error);
        } finally {
          this.isLoading = false;
        }
      },
      formatSalesValue(value) {
        if (value == null) return 'R$ 0';
        if (typeof value === 'number') {
          return 'R$ ' + Utilities.numberWithCommas(Utilities.toDecimalPlaces(value, 2));
        }
        return value || 'R$ 0';
      },
      async loadOffersStats(saasId) {
        try {
          const response = await SaasAPI.getOffersStats(saasId);
          console.log('Offers Stats API Response:', response.data);

          if (response.data) {
            const stats = response.data;

            // First box: offers_count -> Missões Criadas
            if (stats.offers_count !== undefined) {
              this.delivered = stats.offers_count;
              this.$set(this.funnelItems[0], 'title', stats.offers_count);
            }

            // Second box: decisions_accepted_count -> Aceitas
            if (stats.decisions_accepted_count !== undefined) {
              this.$set(this.funnelItems[1], 'title', stats.decisions_accepted_count);
              this.setFunnelPercentage(stats.decisions_accepted_count, 1);
            }

            // Third and Fourth boxes: decisions_validated_count -> Validadas and Concluídas
            if (stats.decisions_validated_count !== undefined) {
              // Third box: Validadas
              this.$set(this.funnelItems[2], 'title', stats.decisions_validated_count);
              this.setFunnelPercentage(stats.decisions_validated_count, 2);

              // Fourth box: Concluídas (same value)
              this.$set(this.funnelItems[3], 'title', stats.decisions_validated_count);
              this.setFunnelPercentage(stats.decisions_validated_count, 3);
            }
          }
        } catch (error) {
          console.error('Error loading offers stats:', error);
        }
      },
      setFunnelPercentage(value, i) {
        // Calculate percentage based on offers_count * usersRegistered
        const total = this.delivered * this.usersRegistered;
        if (total == null || total === 0) {
          this.$set(this.funnelItems[i].detail, 'value', '0%');
          return;
        }
        const detailValue = Math.round((value / total) * 100) || 0;
        this.$set(this.funnelItems[i].detail, 'value', isFinite(detailValue) ? `${detailValue}%` : '0%');
      },
      recalculateFunnelPercentages() {
        // Recalculate all percentages after data is loaded
        if (this.funnelItems[1].title != null) {
          this.setFunnelPercentage(this.funnelItems[1].title, 1);
        }
        if (this.funnelItems[2].title != null) {
          this.setFunnelPercentage(this.funnelItems[2].title, 2);
        }
        if (this.funnelItems[3].title != null) {
          this.setFunnelPercentage(this.funnelItems[3].title, 3);
        }
      },
      getIconStyle(media) {
        if (!media || media.type !== 'icon') return {};
        const color = tinycolor(media.color);
        color.setAlpha(0.2);
        const shadow = media.hasShadow ? `0px 6px 8px -1px ${color.toRgbString()}` : '';
        return {
          boxShadow: shadow,
          backgroundColor: media.color,
          backgroundImage: `url(/svg/metrics/${media.source}.svg)`,
          backgroundSize: '26px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
        };
      },
      initializeTabIndicator() {
        // Try to position the indicator, retry if refs aren't ready yet
        const attempt = () => {
          const navItems = this.getTabNavItems();
          if (!navItems.length) {
            // Refs not ready yet, retry after a short delay
            setTimeout(attempt, 50);
            return;
          }
          const activeTab = this.tabs.find(t => t.isActive) || this.tabs[0];
          const activeIndex = this.tabs.findIndex(t => t.id === activeTab.id);
          if (activeIndex >= 0 && navItems[activeIndex]) {
            window.requestAnimationFrame(() => {
              this.moveTabIndicator(navItems[activeIndex]);
            });
          }
        };
        attempt();
      },
      setupSnapTabs() {
        const navItems = this.getTabNavItems();
        if (!navItems.length) return;
        const activeTab = this.tabs.find(t => t.isActive) || this.tabs[0];
        const activeIndex = this.tabs.findIndex(t => t.id === activeTab.id);
        if (activeIndex < 0 || !navItems[activeIndex]) return;
        window.requestAnimationFrame(() => {
          this.moveTabIndicator(navItems[activeIndex]);
        });
      },
      moveTabIndicator(to) {
        if (!to) return;
        this.tabIndicatorWidth = `${to.offsetWidth}px`;
        this.tabIndicatorOffset = `${to.offsetLeft}px`;
      },
      activateTab(tab) {
        this.tabs.forEach((t) => (t.isActive = false));
        tab.isActive = true;

        // Load data when tab is activated
        if (tab.id === 'consultor' && this.usersList.length === 0) {
          this.loadUsersList();
        }
        if (tab.id === 'registrations') {
          if (this.registrationsData.length === 0) {
            this.loadRegistrationsData();
          } else {
            // Chart data already exists, just recreate the chart
            this.$nextTick(() => {
              setTimeout(() => {
                this.createRegistrationsChart();
              }, 100);
            });
          }
        }
        if (tab.id === 'missions' && !this.missionsData) {
          this.loadMissionsData();
        }

        this.$nextTick(() => {
          const navItems = this.getTabNavItems();
          if (!navItems.length) return;
          const index = this.findActiveTabIndex(tab.id);
          if (index < 0 || !navItems[index]) return;
          window.requestAnimationFrame(() => {
            this.moveTabIndicator(navItems[index]);
          });
        });
      },
      getTabNavItems() {
        if (!this.$refs.tabNavItems) return [];
        return Array.isArray(this.$refs.tabNavItems) ? this.$refs.tabNavItems : [this.$refs.tabNavItems];
      },
      async loadUsersList() {
        try {
          this.isLoadingUsers = true;
          const saasId = this.$route.params.id;
          if (saasId) {
            const response = await SaasAPI.getUsersList(saasId);
            console.log('Users List API Response:', response.data);

            if (response.data && response.data.result) {
              this.usersList = response.data.result;
              // Initialize expanded state
              this.usersList.forEach((_, index) => {
                this.$set(this.usersExpanded, index, false);
              });
            }
          }
        } catch (error) {
          console.error('Error loading users list:', error);
        } finally {
          this.isLoadingUsers = false;
        }
      },
      async toggleUserExpanded(index) {
        const isExpanding = !this.usersExpanded[index];
        this.$set(this.usersExpanded, index, isExpanding);

        // Load stats when expanding
        if (isExpanding && !this.userStats[index]) {
          const user = this.usersList[index];
          if (user && user.b2c_id) {
            await this.loadUserStats(index, user.b2c_id);
          }
        }
      },
      async loadUserStats(index, b2cId) {
        try {
          this.$set(this.isLoadingUserStats, index, true);
          const saasId = this.$route.params.id;
          if (saasId && b2cId) {
            const response = await SaasAPI.getUserStats(saasId, b2cId);
            console.log('User Stats API Response:', response.data);

            if (response.data) {
              // Response structure: { saas_id, b2c_id, clients_count, sales_count, sales_total_value, message }
              const stats = {
                clients_count: response.data.clients_count,
                sales_count: response.data.sales_count,
                sales_total_value: response.data.sales_total_value,
              };
              this.$set(this.userStats, index, stats);
            }
          }
        } catch (error) {
          console.error('Error loading user stats:', error);
        } finally {
          this.$set(this.isLoadingUserStats, index, false);
        }
      },
      formatDate(dateString) {
        if (!dateString) return '-';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch (e) {
          return dateString;
        }
      },
      formatSaleValue(value) {
        if (!value) return '-';
        if (typeof value === 'string') {
          // Handle string values like "1.000" or "2.000"
          const numValue = parseFloat(value.replace('.', ''));
          return 'R$ ' + Utilities.numberWithCommas(Utilities.toDecimalPlaces(numValue, 2));
        }
        if (typeof value === 'number') {
          return 'R$ ' + Utilities.numberWithCommas(Utilities.toDecimalPlaces(value, 2));
        }
        return value;
      },
      findActiveTabIndex(f) {
        return this.tabs.findIndex((t) => t.id === f);
      },
      handleTabNavigation(e) {
        e.preventDefault();
        this.moveTabIndicator(e.currentTarget);
        const activeTabId = this.findActiveTabIndex(e.currentTarget.hash.replace('#', ''));
        if (activeTabId >= 0) {
          this.activateTab(this.tabs[activeTabId]);
        }
      },
      // Registrations chart methods
      async loadRegistrationsData() {
        try {
          this.isLoadingRegistrations = true;
          const saasId = this.$route.params.id;
          if (saasId) {
            const { start_date, end_date } = this.getDateRangeFromPeriod();
            const params = {
              start_date,
              end_date,
              granularity: this.selectedGranularity,
            };
            const response = await SaasAPI.getUserRegistrations(saasId, params);
            console.log('User Registrations API Response:', response.data);

            if (response.data && response.data.registrations) {
              this.registrationsData = response.data.registrations;
              this.calculateSummary(response.data);
              // Wait for DOM to update and tab to be visible
              this.$nextTick(() => {
                setTimeout(() => {
                  this.createRegistrationsChart();
                }, 100);
              });
            }
          }
        } catch (error) {
          console.error('Error loading registrations data:', error);
        } finally {
          this.isLoadingRegistrations = false;
        }
      },
      selectPeriod(period) {
        this.selectedPeriod = period;
        this.loadRegistrationsData();
      },
      selectGranularity(granularity) {
        this.selectedGranularity = granularity;
        this.loadRegistrationsData();
      },
      getDateRangeFromPeriod() {
        const today = new Date();
        let startDate = new Date();

        switch (this.selectedPeriod) {
          case '7d':
            startDate.setDate(today.getDate() - 7);
            break;
          case '30d':
            startDate.setDate(today.getDate() - 30);
            break;
          case '90d':
            startDate.setDate(today.getDate() - 90);
            break;
          case 'month':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1);
            break;
          default:
            startDate.setDate(today.getDate() - 30);
        }

        return {
          start_date: startDate.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
        };
      },
      createRegistrationsChart() {
        if (!this.$refs.registrationsChart) {
          console.warn('Canvas ref not available yet');
          return;
        }

        // Handle ref as array (Vue returns array when ref is inside v-for)
        let canvas = this.$refs.registrationsChart;
        if (Array.isArray(canvas)) {
          canvas = canvas[0];
        }

        // Check if we have a valid canvas element
        if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
          console.warn('Ref is not a canvas element', canvas);
          return;
        }

        // Destroy existing chart if it exists
        if (this.chartInstance) {
          this.chartInstance.destroy();
        }

        const ctx = canvas.getContext('2d');
        const labels = this.registrationsData.map(item => this.formatChartDate(item.date));
        const data = this.registrationsData.map(item => item.count);

        this.chartInstance = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [{
              label: this.$t('saas.statistics.registrations_count_label'),
              data: data,
              backgroundColor: 'rgba(35, 85, 195, 0.7)',
              borderColor: 'rgba(35, 85, 195, 1)',
              borderWidth: 1,
              borderRadius: 4,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              yAxes: [{
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                  fontFamily: 'Silka Regular',
                  fontSize: 12,
                },
                gridLines: {
                  color: 'rgba(0, 0, 0, 0.05)',
                }
              }],
              xAxes: [{
                ticks: {
                  fontFamily: 'Silka Regular',
                  fontSize: 12,
                  maxRotation: 45,
                  minRotation: 0,
                },
                gridLines: {
                  display: false,
                }
              }]
            },
            legend: {
              display: false,
            },
            tooltips: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleFontFamily: 'Silka Semibold',
              bodyFontFamily: 'Silka Regular',
              cornerRadius: 4,
              callbacks: {
                title: (tooltipItems) => {
                  return this.registrationsData[tooltipItems[0].index].date;
                },
                label: (tooltipItem) => {
                  return `${this.$t('saas.statistics.registrations_count_label')}: ${tooltipItem.value}`;
                }
              }
            }
          }
        });
      },
      calculateSummary(data) {
        if (data.summary) {
          this.registrationsSummary.total = data.summary.total || 0;
          this.registrationsSummary.average = data.summary.average_per_day || 0;
        } else {
          // Calculate manually if not provided
          const counts = this.registrationsData.map(item => item.count);
          this.registrationsSummary.total = counts.reduce((sum, count) => sum + count, 0);
          this.registrationsSummary.average = this.registrationsSummary.total / (counts.length || 1);
        }
        this.registrationsSummary.peak = Math.max(...this.registrationsData.map(item => item.count), 0);
      },
      formatChartDate(dateString) {
        const date = new Date(dateString + 'T00:00:00');
        if (this.selectedGranularity === 'monthly') {
          return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
        } else if (this.selectedGranularity === 'weekly') {
          return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
        }
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
      },
      // Missions methods
      async loadMissionsData() {
        try {
          this.isLoadingMissions = true;
          const saasId = this.$route.params.id;
          if (saasId) {
            const response = await SaasAPI.getLevelsOffers(saasId);
            console.log('Missions/Levels Offers API Response:', response.data);
            this.missionsData = response.data;
            // Initialize expanded state for all levels
            if (this.missionsData && this.missionsData.levels) {
              this.missionsData.levels.forEach((_, index) => {
                this.$set(this.levelsExpanded, index, false);
              });
            }
          }
        } catch (error) {
          console.error('Error loading missions data:', error);
          this.missionsData = { error: error.message };
        } finally {
          this.isLoadingMissions = false;
        }
      },
      toggleLevelExpanded(index) {
        this.$set(this.levelsExpanded, index, !this.levelsExpanded[index]);
      },
      getLevelTotalAccepted(offers) {
        return offers.reduce((sum, offer) => sum + (offer.decisions_accepted_count || 0), 0);
      },
      getLevelTotalValidated(offers) {
        return offers.reduce((sum, offer) => sum + (offer.decisions_validated_count || 0), 0);
      },
      getSortedOffers(offers) {
        // Sort by ranking in descending order
        return [...offers].sort((a, b) => {
          const rankingA = parseFloat(a.ranking) || 0;
          const rankingB = parseFloat(b.ranking) || 0;
          return rankingB - rankingA;
        });
      },
      getOfferStatusText(status_id) {
        if (!status_id) return '';
        const statusKey = SetOfferStatus(status_id);
        const translationKey = 'offers.' + statusKey;
        return this.$te(translationKey) ? this.$t(translationKey) : '';
      },
    },
  };
</script>

<style lang="scss">
  .saas-statistics.components-wrapper {
    .components {
      overflow-y: auto !important;
      overflow-x: hidden !important;
      max-height: calc(100vh - 120px) !important;
    }
  }

  .saas-statistics .metric-cards-row {
    width: 100%;
    height: 100%;
    padding: 0.93rem;
    display: flex;
    flex-wrap: nowrap;
    gap: 0.93rem;
    margin-top: 24px;
    box-sizing: border-box;

    .metric-card {
      flex: 1;
      height: 100%;
      min-height: 120px;
      border-radius: 0.687rem;
      padding: 0.81em 1.06rem;
      display: flex;
      gap: 0.5rem;
      flex-direction: column;
      justify-content: space-around;
      background-color: rgba(#ff7a00, 0.1);

      &:nth-child(2) {
        background-color: rgba(#00ba88, 0.1);
      }

      &:nth-child(3) {
        background-color: rgba(#7494ea, 0.1);
      }

      &:nth-child(4) {
        background-color: rgba(#9ebe0e, 0.1);
      }

      strong,
      span {
        display: block;
        line-height: 100%;
        background: transparent;
        background-color: transparent;
      }

      strong {
        font-family: 'Silka Semibold';
        font-size: 1.43rem;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 0;
        text-decoration: none;
        background: transparent !important;
        background-color: transparent !important;

        &.is-loading {
          opacity: 0.5;
        }
      }

      span {
        font-family: 'Silka Regular';
        font-size: 0.687rem;
        font-weight: 400;
        text-decoration: none;
        background: transparent !important;
        background-color: transparent !important;
      }
    }
  }

  .funnel-section {
    margin-top: 24px;
    width: 100%;
    padding: 0 0.93rem;
    box-sizing: border-box;

    .saas-funnel-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 0;
      position: relative;
      width: 100%;

      @media screen and (max-width: 967px) {
        flex-direction: column;
        gap: 1rem;
      }
    }

    .funnel-card {
      position: relative;
      flex: 1;
      display: flex;
      margin-right: 3.75rem;

      &:last-child {
        margin-right: 0;
      }

      &.has-connector::before {
        content: '';
        position: absolute;
        top: 50%;
        left: -3.75rem;
        width: 3.75rem;
        height: 1.25rem;
        transform: translateY(-50%);
        background: linear-gradient(269.99deg, rgba(#72f3d1, 0.6) 14%, rgba(#50dbc1, 0.6) 64%);
        z-index: 1;
      }

      @media screen and (max-width: 967px) {
        margin-right: 0 !important;
        width: 100%;
        flex: 1 1 100%;

        &.has-connector::before {
          display: none;
        }
      }
    }

    .funnel-card-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: white;
      border-radius: 0.75rem;
      padding: 1.25rem;
      width: 100%;
      position: relative;

      &:has(.funnel-text) {
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 1rem;
      }
    }

    .funnel-icon {
      width: 3rem;
      height: 3rem;
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .funnel-text {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .funnel-value {
      font-family: 'Silka Semibold';
      font-size: 1.43rem;
      font-weight: 700;
      color: #0a1735;
      line-height: 1.2;
      margin-bottom: 0.25rem;
    }

    .funnel-label {
      font-family: 'Silka Regular';
      font-size: 0.687rem;
      font-weight: 400;
      color: #0a1735;
      opacity: 0.7;
      line-height: 1.2;
    }

    .funnel-percentage {
      padding: 0.25rem 0.5rem;
      border-radius: 0.375rem;
      font-family: 'Silka Semibold';
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;
      margin-left: auto;
    }

    @media screen and (max-width: 967px) {
      .funnel-card-content {
        flex-direction: row !important;
        width: 100%;
      }
    }
  }

  .tabs-section {
    margin-top: 24px;
    width: 100%;
    background: white;
    border-radius: 0.75rem;
    padding: 0;

    .modatta-snap-tabs {
      width: 100%;

      .snap-tabs-header {
        position: relative;
        padding: 0;
        padding-top: 0.5rem;
        border-bottom: 1px solid #dee5f6;
        margin-bottom: 0;
      }

      .snap-tabs-nav {
        position: relative;
        display: flex;
        gap: 0;
        padding: 0 0.93rem;
        height: 3.5rem;

        .nav-indicator {
          position: absolute;
          bottom: -1.5px;
          height: 3px;
          background: #2355c3;
          transition: all 0.3s ease;
          border-radius: 3px 3px 0 0;
          z-index: 1;
        }

        > div {
          flex: 0 0 auto;

          a {
            display: flex;
            align-items: center;
            height: 100%;
            padding: 0;
            padding-left: 1.25rem;
            padding-right: 1.25rem;
            padding-bottom: 0.19rem;
            font-size: 0.875rem;
            color: #6b7280;
            text-decoration: none;
            border: 0;
            border-radius: 0;
            border-bottom: 3px solid transparent;
            transition: all 0.3s ease;
            white-space: nowrap;
            line-height: 100%;
            background-color: transparent;

            &:hover {
              color: #2355c3;
            }

            &.active {
              color: #2355c3;
              font-family: 'Silka Semibold';
              font-weight: 600;
            }
          }
        }
      }

      .snap-tabs-body {
        padding: 0;
        min-height: 200px;

        .snap-tab {
          width: 100%;
        }

        .tab-content-placeholder {
          padding: 1rem;
          color: #6b7280;
          text-align: center;
        }
      }
    }
  }

  .consultor-tab-content {
    width: 100%;
    padding: 1.25rem 0.93rem;

    .users-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .modatta-accordion.user-item {
      border: 1px solid #dee5f6;
      border-radius: 0.75rem;
      background: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;

      &.is-open {
        border-color: #2355c3;
        box-shadow: 0 2px 6px rgba(35, 85, 195, 0.1);
      }

      .accordion-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 1.25rem;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background-color: rgba(35, 85, 195, 0.02);
        }

        .user-header-content {
          flex: 1;

          .user-info-row {
            display: flex;
            flex-wrap: wrap;
            gap: 1.5rem;

            .user-info-item {
              display: flex;
              align-items: center;
              gap: 0.5rem;

              .info-label {
                font-family: 'Silka Semibold';
                font-size: 0.875rem;
                color: #6b7280;
                white-space: nowrap;
              }

              .info-value {
                font-family: 'Silka Regular';
                font-size: 0.875rem;
                color: #1f2937;
              }
            }
          }
        }

        .modatta-icon {
          flex-shrink: 0;
          transition: transform 0.3s ease;

          img {
            width: 16px;
            height: 16px;
          }
        }
      }

      &.is-open .accordion-header .modatta-icon {
        transform: rotate(180deg);
      }

      .accordion-body {
        border-top: 1px solid #dee5f6;
        padding: 1.25rem;
        background-color: #f9fafb;

        .user-details {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;

          .loading-stats {
            padding: 1rem 0;

            .loading-row {
              height: 20px;
              background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
              background-size: 200% 100%;
              animation: loading 1.5s ease-in-out infinite;
              border-radius: 4px;
            }
          }

          @keyframes loading {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }

          .detail-section {
            .section-name {
              font-family: 'Silka Semibold';
              font-size: 0.875rem;
              color: #1f2937;
              margin-bottom: 0.75rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .detail-row {
              display: flex;
              align-items: flex-start;
              gap: 0.5rem;
              padding: 0.5rem 0;
              border-bottom: 1px solid #e5e7eb;

              &:last-child {
                border-bottom: none;
              }

              .detail-label {
                font-family: 'Silka Semibold';
                font-size: 0.875rem;
                color: #6b7280;
                min-width: 120px;
                flex-shrink: 0;
              }

              .detail-value {
                font-family: 'Silka Regular';
                font-size: 0.875rem;
                color: #1f2937;
                flex: 1;
              }
            }
          }
        }
      }
    }

    .empty-state {
      padding: 3rem 1rem;
      text-align: center;
      color: #6b7280;
      font-size: 0.875rem;
    }
  }

  .registrations-tab-content {
    width: 100%;
    padding: 1.25rem 0.93rem;

    .registrations-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;
      gap: 1rem;
      flex-wrap: wrap;

      @media screen and (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
      }

      .quick-filters,
      .granularity-controls {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
      }

      .filter-btn,
      .granularity-btn {
        padding: 0.5rem 1rem;
        border: 1px solid #dee5f6;
        background: white;
        color: #6b7280;
        border-radius: 0.5rem;
        font-family: 'Silka Regular';
        font-size: 0.875rem;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          border-color: #2355c3;
          color: #2355c3;
          background-color: rgba(35, 85, 195, 0.05);
        }

        &.active {
          background: #2355c3;
          color: white;
          border-color: #2355c3;
          font-family: 'Silka Semibold';
        }
      }
    }

    .chart-loading {
      min-height: 400px;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 1rem;

      .loading-row {
        height: 40px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: loading 1.5s ease-in-out infinite;
        border-radius: 4px;
      }
    }

    @keyframes loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }

    .chart-container {
      background: white;
      border-radius: 0.75rem;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

      .chart-wrapper {
        position: relative;
        height: 400px;
        width: 100%;

        @media screen and (max-width: 768px) {
          height: 300px;
        }
      }
    }

    .summary-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
      margin-top: 1.5rem;

      @media screen and (max-width: 768px) {
        grid-template-columns: 1fr;
      }

      .summary-card {
        background: white;
        border-radius: 0.75rem;
        padding: 1.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        text-align: center;

        .summary-value {
          font-family: 'Silka Semibold';
          font-size: 2rem;
          font-weight: 700;
          color: #2355c3;
          margin-bottom: 0.5rem;
          line-height: 1;
        }

        .summary-label {
          font-family: 'Silka Regular';
          font-size: 0.875rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        &:nth-child(1) .summary-value {
          color: #2355c3;
        }

        &:nth-child(2) .summary-value {
          color: #19D2B4;
        }

        &:nth-child(3) .summary-value {
          color: #ff7a00;
        }
      }
    }

    .empty-state {
      padding: 3rem 1rem;
      text-align: center;
      color: #6b7280;
      font-size: 0.875rem;
    }
  }

  .missions-tab-content {
    width: 100%;
    padding: 1.25rem 0.93rem;

    .missions-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .modatta-accordion.level-item {
      border: 1px solid #dee5f6;
      border-radius: 0.75rem;
      background: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;
      overflow: hidden;
      width: 100%;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;

      &.is-open {
        border-color: #2355c3;
        box-shadow: 0 2px 6px rgba(35, 85, 195, 0.1);

        .accordion-header {
          background-color: #f0f5ff;
          border-bottom: 2px solid #2355c3;
          border-radius: 0.75rem 0.75rem 0 0;
        }
      }

      .accordion-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 1.25rem 1.5rem;
        cursor: pointer;
        transition: all 0.3s ease;
        border-radius: 0.75rem;
        box-sizing: border-box;
        flex-shrink: 0;

        &:hover {
          background-color: rgba(35, 85, 195, 0.02);
        }

        .level-header-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-width: 0;
          gap: 2rem;
          width: 100%;

          @media screen and (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .level-title {
            display: flex;
            align-items: baseline;
            gap: 0.5rem;
            flex-shrink: 0;

            .level-name {
              font-family: 'Silka Semibold';
              font-size: 1.125rem;
              color: #1f2937;
              font-weight: 600;
            }

            .level-missions-count {
              font-family: 'Silka Regular';
              font-size: 0.875rem;
              color: #6b7280;
            }
          }

          .level-stats {
            display: flex;
            gap: 2rem;
            flex-shrink: 0;

            @media screen and (max-width: 768px) {
              width: 100%;
              justify-content: flex-start;
            }

            .stat-item {
              display: flex;
              align-items: center;
              gap: 0.5rem;

              .stat-label {
                font-family: 'Silka Regular';
                font-size: 0.875rem;
                color: #6b7280;
              }

              .stat-value {
                font-family: 'Silka Semibold';
                font-size: 1rem;
                color: #2355c3;
                font-weight: 600;
              }
            }
          }
        }

        .modatta-icon {
          flex-shrink: 0;
          transition: transform 0.3s ease;
          margin-left: 1rem;

          img {
            width: 16px;
            height: 16px;
          }
        }
      }

      &.is-open .accordion-header .modatta-icon {
        transform: rotate(180deg);
      }

      .accordion-body {
        padding: 1.5rem;
        background-color: #f9fafb;
        flex: 1;
        width: 100%;
        box-sizing: border-box;

        .empty-missions {
          padding: 2rem 1rem;
          text-align: center;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .missions-cards {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          width: 100%;
          box-sizing: border-box;

          .mission-card {
            background: white;
            border: 1px solid #dee5f6;
            border-radius: 0.5rem;
            padding: 1.25rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: all 0.2s ease;
            width: 100%;
            box-sizing: border-box;

            &:hover {
              border-color: #2355c3;
              box-shadow: 0 1px 4px rgba(35, 85, 195, 0.1);
            }

            @media screen and (max-width: 768px) {
              flex-direction: column;
              align-items: flex-start;
              gap: 1rem;
            }

            .mission-name {
              font-family: 'Silka Semibold';
              font-size: 1rem;
              color: #1f2937;
              flex: 1;
              font-weight: 500;
              padding-right: 2rem;
              min-width: 0;
            }

            .mission-stats {
              display: flex;
              align-items: center;
              gap: 2.5rem;
              flex-shrink: 0;
              margin-left: auto;

              @media screen and (max-width: 768px) {
                width: 100%;
                justify-content: space-between;
                margin-left: 0;
              }

              .mission-stat {
                display: flex;
                flex-direction: column;
                align-items: center;
                min-width: 80px;

                .mission-stat-label {
                  font-family: 'Silka Regular';
                  font-size: 0.75rem;
                  color: #6b7280;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  margin-bottom: 0.375rem;
                }

                .mission-stat-value {
                  font-family: 'Silka Semibold';
                  font-size: 1.25rem;
                  color: #2355c3;
                  font-weight: 600;
                }
              }

              .mission-status {
                .status-badge {
                  display: inline-block;
                  padding: 0.375rem 0.75rem;
                  border-radius: 0.375rem;
                  font-family: 'Silka Semibold';
                  font-size: 0.75rem;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  white-space: nowrap;

                  &.status-ost1 {
                    background-color: rgba(107, 114, 128, 0.1);
                    color: #6b7280;
                  }

                  &.status-ost2 {
                    background-color: rgba(251, 191, 36, 0.1);
                    color: #d97706;
                  }

                  &.status-ost3 {
                    background-color: rgba(251, 191, 36, 0.1);
                    color: #d97706;
                  }

                  &.status-ost4 {
                    background-color: rgba(34, 197, 94, 0.1);
                    color: #16a34a;
                  }

                  &.status-ost5 {
                    background-color: rgba(34, 197, 94, 0.1);
                    color: #16a34a;
                  }

                  &.status-ost6 {
                    background-color: rgba(107, 114, 128, 0.1);
                    color: #6b7280;
                  }

                  &.status-ost7 {
                    background-color: rgba(239, 68, 68, 0.1);
                    color: #dc2626;
                  }

                  &.status-ost8 {
                    background-color: rgba(251, 191, 36, 0.1);
                    color: #d97706;
                  }

                  &.status-ost9 {
                    background-color: rgba(239, 68, 68, 0.1);
                    color: #dc2626;
                  }
                }
              }
            }
          }
        }
      }
    }

    .empty-state {
      padding: 3rem 1rem;
      text-align: center;
      color: #6b7280;
      font-size: 0.875rem;
    }
  }
</style>

